<template>
    <v-container>
        <v-row justify="center">
            <v-col cols="12" sm="12" md="12">
                <v-card class="pa-4" elevation="4">
                    <v-card-title>
                        <h1 class="text-h5 font-weight-bold text-grey-darken-4">
                            {{ isEditing ? 'Editar Categoria' : 'Nova Categoria' }}
                        </h1>
                    </v-card-title>
                    <v-card-text>

                        <v-form ref="formRef" @submit.prevent="handleSubmit">
                            <v-row class="mt-2">
                                <v-col cols="12">
                                    <v-text-field v-model="formValue.description" label="Descrição da Categoria"
                                        :rules="[rules.description]" required></v-text-field>
                                </v-col>

                                <v-col cols="12" sm="6" md="4">
                                    <v-switch v-model="formValue.is_active" color="success"
                                        label="Categoria Ativa?"></v-switch>
                                </v-col>

                                <v-col cols="12" sm="6" md="4">
                                    <v-switch v-model="formValue.is_employability" color="primary"
                                        label="Categoria de Empregabilidade?"></v-switch>
                                </v-col>

                                <v-col cols="12">
                                    <v-text-field v-model="subcategoryInput" label="Adicionar Subcategoria"
                                        @keyup.enter="addSubcategory" clearable></v-text-field>

                                    <div class="mt-2 d-flex flex-wrap ga-2">
                                        <v-chip v-for="(item, index) in formValue.subcategories" :key="index" closable
                                            color="primary" @click:close="removeSubcategory(index)">
                                            {{ item }}
                                        </v-chip>
                                    </div>
                                </v-col>

                            </v-row>
                        </v-form>

                    </v-card-text>

                    <v-card-actions class="d-flex justify-space-between mt-4">
                        <v-btn color="secondary" variant="flat" @click="handleCancel">
                            <v-icon left>mdi-arrow-left</v-icon>
                            Voltar para Lista
                        </v-btn>
                        <v-btn color="primary" variant="flat" :loading="isSubmitting" :disabled="isSubmitting"
                            @click="handleSubmit">
                            <v-icon left>mdi-content-save</v-icon>
                            {{ isEditing ? 'Salvar Alterações' : 'Cadastrar Categoria' }}
                        </v-btn>
                    </v-card-actions>
                </v-card>
            </v-col>
        </v-row>
    </v-container>
</template>

<script setup>
import { ref, computed, onMounted, nextTick } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import api from '@/services/api';
import { useToastStore } from '@/store/toast';
import { useLoadingStore } from '@/store/loadingStore';

const toastStore = useToastStore();
const loadingStore = useLoadingStore();
const route = useRoute();
const router = useRouter();
const formRef = ref(null);

const isSubmitting = ref(false);

const formValue = ref({
    description: '',
    is_active: true,
    is_employability: false,
    subcategories: []
});

const isEditing = computed(() => !!route.params.id);

const rules = {
    description: v => !!v || 'A descrição é obrigatória.',
};

const subcategoryInput = ref("");

const addSubcategory = () => {
    const value = subcategoryInput.value.trim();
    if (value && !formValue.value.subcategories.includes(value)) {
        formValue.value.subcategories.push(value);
    }
    subcategoryInput.value = "";
};

const removeSubcategory = (index) => {
    formValue.value.subcategories.splice(index, 1);
};

const loadCategory = async (id) => {
    loadingStore.show();
    try {
        const response = await api.get(`/categories/${id}`);
        const data = response.data.data;

        if (data) {
            formValue.value = {
                description: data.description,
                is_active: data.is_active === 1,
                is_employability: data.is_employability === 1,
                subcategories: Array.isArray(data.subcategories) ? data.subcategories : []
            };

            await nextTick();
            if (formRef.value) {
                formRef.value.resetValidation();
            }
        } else {
            toastStore.showToast({
                message: 'A categoria não foi encontrada.',
                color: 'warning',
            });
        }
    } catch (error) {
        console.error('Erro ao carregar dados da categoria:', error);
        toastStore.showToast({
            message: error?.response?.data?.error || 'Erro ao carregar dados da categoria.',
            color: 'error',
        });
    } finally {
        loadingStore.hide();
    }
};

const handleSubmit = async () => {
    const { valid } = await formRef.value.validate();
    if (!valid) {
        return;
    }

    isSubmitting.value = true;

    // Prepara o payload
    const payload = {
        description: formValue.value.description,
        is_active: formValue.value.is_active ? 1 : 0,
        is_employability: formValue.value.is_employability ? 1 : 0,
        subcategories: formValue.value.subcategories
    };

    try {
        if (isEditing.value) {
            await api.put(`/categories/${route.params.id}`, payload);
        } else {
            await api.post('/categories', payload);
        }

        router.push('/app/categorias');
        toastStore.showToast({
            message: isEditing.value ? 'Categoria atualizada com sucesso!' : 'Categoria cadastrada com sucesso!',
            color: 'success',
        });

    } catch (error) {
        isSubmitting.value = false;
        console.error('Erro ao salvar categoria:', error);
        toastStore.showToast({
            message: error?.response?.data?.error || 'Erro ao salvar categoria.',
            color: 'error',
        });
    }
};

const handleCancel = () => {
    router.push('/app/categorias');
};

onMounted(() => {
    if (isEditing.value) {
        loadCategory(route.params.id);
    }
});
</script>